import {
  Box,
  Container,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';

import { useEffect, useState } from 'react';

import MixImportPanel from './components/MixImportPanel';
import MixDashboardCards from './components/MixDashboardCards';
import MixDeliveriesTable from './components/MixDeliveriesTable';
import MixStockSummary from './components/MixStockSummary';
import MixRecipientConfigsTable from './components/MixRecipientConfigsTable';
import AddMissingProductDialog from './components/AddMissingProductDialog';

import MixReports from './components/MixReports';
import { MIX_IMPORT_STATUS } from './types/mix-deliveries.types';

import { resumeMixImport } from '../../../api/mix-deliveriesApi';

const MIX_TABS = {
  IMPORT: 'IMPORT',
  OPERATIONS: 'OPERATIONS',
  REPORTS: 'REPORTS',
  RECIPIENT_CONFIGS: 'RECIPIENT_CONFIGS',
};

export default function MixDeliveriesPage({ userRole }) {
  const normalizedRole = String(userRole ?? '').toUpperCase();

  const isOperator = normalizedRole === 'OPERATOR';

  const canManageRecipientConfigs =
    normalizedRole === 'ADMIN' || normalizedRole === 'KONTROLER';

  const canViewReports =
    normalizedRole === 'ADMIN' || normalizedRole === 'KONTROLER';

  /*
   * Operator od razu trafia na listę miksów.
   * Pozostali użytkownicy domyślnie widzą import.
   */
  const [activeTab, setActiveTab] = useState(
    isOperator ? MIX_TABS.OPERATIONS : MIX_TABS.IMPORT,
  );

  const [refreshKey, setRefreshKey] = useState(0);

  const [importRefreshKey, setImportRefreshKey] = useState(0);

  const [reportRefreshKey, setReportRefreshKey] = useState(0);

  const [recipientConfigRefreshKey, setRecipientConfigRefreshKey] = useState(0);

  const [missingProductDialog, setMissingProductDialog] = useState({
    product: null,
    importId: null,
  });

  const [selectedDeliveryIds, setSelectedDeliveryIds] = useState([]);

  const [availableDeliveryCount, setAvailableDeliveryCount] = useState(0);

  /*
   * Pusty zestaw oznacza wszystkie dostawy.
   *
   * Gdy zaznaczono wszystkie dostępne dostawy,
   * również nie wysyłamy filtra do backendu.
   */
  const selectedDeliveryIdsForSummary =
    selectedDeliveryIds.length > 0 &&
    selectedDeliveryIds.length < availableDeliveryCount
      ? selectedDeliveryIds
      : [];

  /*
   * Zabezpieczenie na wypadek zmiany użytkownika
   * albo późniejszego wczytania roli.
   */
  useEffect(() => {
    if (isOperator && activeTab !== MIX_TABS.OPERATIONS) {
      setActiveTab(MIX_TABS.OPERATIONS);
      return;
    }

    if (!canViewReports && activeTab === MIX_TABS.REPORTS) {
      setActiveTab(MIX_TABS.OPERATIONS);
      return;
    }

    if (
      !canManageRecipientConfigs &&
      activeTab === MIX_TABS.RECIPIENT_CONFIGS
    ) {
      setActiveTab(MIX_TABS.OPERATIONS);
    }
  }, [isOperator, canViewReports, canManageRecipientConfigs, activeTab]);

  /*
   * Zmiana danych operacyjnych może wpływać
   * również na aktualny stan wpisów raportowych,
   * np. wymaga sprawdzenia, aktywność lub rewizję.
   */
  const refreshOperationalData = () => {
    setRefreshKey((value) => value + 1);
    setReportRefreshKey((value) => value + 1);
  };

  const refreshImportData = () => {
    setImportRefreshKey((value) => value + 1);
  };

  const refreshRecipientConfigs = () => {
    setRecipientConfigRefreshKey((value) => value + 1);
  };

  const handleAddProduct = (product, importId) => {
    setMissingProductDialog({
      product,
      importId,
    });
  };

  const handleCloseProductDialog = () => {
    setMissingProductDialog({
      product: null,
      importId: null,
    });
  };

  const handleProductSaved = async () => {
    const importId = missingProductDialog.importId;

    if (!importId) {
      handleCloseProductDialog();
      return;
    }

    const result = await resumeMixImport(importId);

    handleCloseProductDialog();
    refreshImportData();

    if (result.status === MIX_IMPORT_STATUS.COMPLETED) {
      refreshOperationalData();

      setActiveTab(MIX_TABS.OPERATIONS);
    }
  };

  const handleAnalysisCompleted = () => {
    refreshOperationalData();
    refreshImportData();
    refreshRecipientConfigs();

    setActiveTab(MIX_TABS.OPERATIONS);
  };

  const handleRecipientConfigChanged = () => {
    refreshOperationalData();
    refreshRecipientConfigs();
  };

  const handleTabChange = (_event, newValue) => {
    if (isOperator && newValue === MIX_TABS.IMPORT) {
      return;
    }

    if (!canViewReports && newValue === MIX_TABS.REPORTS) {
      return;
    }

    if (!canManageRecipientConfigs && newValue === MIX_TABS.RECIPIENT_CONFIGS) {
      return;
    }

    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Stack spacing={3}>
        <Paper
          variant="outlined"
          sx={{
            p: {
              xs: 2,
              md: 3,
            },
          }}
        >
          <Typography variant="h4" component="h1" fontWeight={700}>
            Zarządzanie miksami
          </Typography>

          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            {isOperator
              ? 'Obsługa miksów do przygotowania.'
              : 'Import zapasu, analiza dostaw oraz obsługa pozycji miksowych.'}
          </Typography>
        </Paper>

        <Paper variant="outlined">
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {!isOperator && (
              <Tab value={MIX_TABS.IMPORT} label="Import danych" />
            )}

            <Tab value={MIX_TABS.OPERATIONS} label="Miksy do przygotowania" />

            {canViewReports && <Tab value={MIX_TABS.REPORTS} label="Raporty" />}

            {canManageRecipientConfigs && (
              <Tab
                value={MIX_TABS.RECIPIENT_CONFIGS}
                label="Konfiguracja odbiorców"
              />
            )}
          </Tabs>
        </Paper>

        {!isOperator && activeTab === MIX_TABS.IMPORT && (
          <MixImportPanel
            refreshKey={importRefreshKey}
            onAddProduct={handleAddProduct}
            onAnalysisCompleted={handleAnalysisCompleted}
          />
        )}

        {activeTab === MIX_TABS.OPERATIONS && (
          <Stack spacing={3}>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                Miksy do przygotowania
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Dostawy 8AAA zawierające pozycje wymagające przygotowania w
                strefie kompletacji.
              </Typography>
            </Box>

            <MixDashboardCards refreshKey={refreshKey} />

            {/* <MixStockSummary refreshKey={refreshKey} /> */}

            <MixStockSummary
              refreshKey={refreshKey}
              selectedDeliveryIds={selectedDeliveryIdsForSummary}
              selectedDeliveryCount={selectedDeliveryIds.length}
              availableDeliveryCount={availableDeliveryCount}
            />
            {/* 
            <MixDeliveriesTable
              refreshKey={refreshKey}
              userRole={normalizedRole}
              onDataChanged={refreshOperationalData}
            /> */}
            <MixDeliveriesTable
              refreshKey={refreshKey}
              userRole={normalizedRole}
              selectedDeliveryIds={selectedDeliveryIds}
              onSelectedDeliveryIdsChange={setSelectedDeliveryIds}
              onTotalDeliveriesChange={setAvailableDeliveryCount}
              onDataChanged={refreshOperationalData}
            />
          </Stack>
        )}

        {canViewReports && activeTab === MIX_TABS.REPORTS && (
          <MixReports refreshKey={reportRefreshKey} />
        )}

        {canManageRecipientConfigs &&
          activeTab === MIX_TABS.RECIPIENT_CONFIGS && (
            <Stack spacing={3}>
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Konfiguracja odbiorców
                </Typography>

                <Typography variant="body2" color="text.secondary">
                  Zarządzaj wymaganiem kanapkowania dla odbiorców. Zmiana może
                  zaktualizować aktywne dostawy.
                </Typography>
              </Box>

              <MixRecipientConfigsTable
                refreshKey={recipientConfigRefreshKey}
                onDataChanged={handleRecipientConfigChanged}
              />
            </Stack>
          )}

        {!isOperator && (
          <AddMissingProductDialog
            open={Boolean(missingProductDialog.product)}
            product={missingProductDialog.product}
            onClose={handleCloseProductDialog}
            onSaved={handleProductSaved}
          />
        )}
      </Stack>
    </Container>
  );
}
